import type { ProductServiceItemProps } from '@/Application/Components/ProductList/ProductItem/ProductExtraList/ProductServiceList/ProductServiceItem/ProductsServiceItem';
import type {
  SHIPPING_METHOD_TYPE,
  THomeDeliveryServiceName
} from '@/modules/perseo-billing/constants/src';
import type { EServiceType, IPrice } from '@/modules/perseo-billing/interfaces';

import type { ICustomFields, ITimeSlot } from './orderV2';

export interface ICoupon {
  id?: string;
  code?: string;
  description?: string;
  discount?: number;
  discountFormat?: string;
}

export interface IBillingSummary {
  basketId?: string;
  totalItems?: number;
  items?: TBillingSummaryProductItem[];
  summary?: Summary;
  coupons?: ICoupon[];
  deliveries?: TBillingSummaryDeliveries;
  novios?: INoviosDetails;
  hasIntangible?: boolean;
  isMarketplaceEasy?: boolean;
  customerInfo?: ISummaryCustomerInfo;
  clubParis?: IClubParis;
  giftCard?: IGiftCardSummary;
}

export interface IClubParis {
  code: string;
  type: ClubType;
  names?: string;
  babyName?: string;
}

export enum ClubType {
  WEDDING = 'WEDDING',
  BABY = 'BABY',
  HOME = 'HOME'
}

export interface ISummaryCustomerInfo {
  customerName?: string;
  customerRut?: string;
  customerEmail?: string;
  customerPhone?: string;
}

export interface INoviosDetails {
  isNovios?: boolean;
  code?: string;
  nameFirstPerson?: string;
  nameSecondPerson?: string;
}

export interface Item {
  sku?: string;
  img?: string;
  title?: string;
  quantity?: number;
  specification?: string;
  prices?: IPrice[];
  gtm?: IItemGTM;
  isSampling?: boolean;
  isSecondHand?: boolean;
  isPresale?: boolean;
  isColabPrice?: boolean;
  isService?: boolean;
  serviceType?: EServiceType;
  isEmailTrigger?: boolean;
  options?: Item[];
  // for option items
  duration?: string;
  priceFormat?: string;
}

export interface IItemGTM {
  masterId: string;
  brand: string;
  primaryCategoryId: string;
  departmentId: string;
  departmentName: string;
  subDepartmentName: string;
  subDepartmentId: string;
  claseName: string;
  claseId: string;
  size: string;
  price: number;
  referencePrice: number;
  cencosudPrice: number; // dimension20
  rating: string;
  reviewsCount: number;
  hasWarranty: boolean;
  shippingType: string;
  isCmedia: boolean;
}

// type TotalPrice = {
//   id: string;
//   label: string;
//   price: number;
//   formattedPrice: string;
// };

export interface Summary {
  totalPrices: ITotalPricesSummary[];
  totalPriceValue?: number;
  totalPrice?: string;
  totalPriceCencoPay?: string;
  totalPriceCencosud?: string;
  totalPriceCencoPayWithCencosud?: string | null;
  totalPriceOtherPaymentMethod?: string;
  deliveryPrices?: DeliveryPrices;
  totalDiffCountCoupon?: string;
  totalDiffCencosud?: string;
  totalDiffCencoPay?: string;
  totalDiffCountGiftCard?: string;
  totalPriceCencopay?: string;
}

export interface DeliveryPrices {
  totalDeliveryPrice?: string;
  deliveryPriceList?: IDeliveryPriceList[];
}

export interface IDeliveryPriceList {
  type: string;
  priceFormatter: string;
}

export interface Coupon {
  id?: string;
  totalDiscount?: string;
  title?: string;
}

export interface IGiftCardSummary {
  paymentInstrumentId?: string;
  paymentMethodId?: string;
  cardNumber?: string;
  discount?: number;
  discountFormat?: string;
  isTotal?: boolean;
  mountDiff?: number;
  mountDiffFormatter?: string;
  numberLastDigits?: string;
  isNovios?: boolean;
  balance?: string;
}

export interface ITotalPricesSummary {
  id: string;
  label: string;
  price: number;
  formattedPrice: string;
}

type TBillingDeliveryMethodTypes = {
  homeDelivery: THomeDelivery;
  pickUp: TPickupDelivery;
  intangible: TIntangibleDelivery;
  novios: TNoviosDelivery;
};

type TBillingDeliveries = {
  [K in (typeof SHIPPING_METHOD_TYPE)[keyof typeof SHIPPING_METHOD_TYPE]]: TBillingDeliveryMethodTypes[K][];
};

type TDeliveriesType = TBillingDeliveries[keyof TBillingDeliveries][number];

type THomeDelivery = {
  shippingMethodType: string;
  shippingServiceName: THomeDeliveryServiceName;
  addressAlias: string;
  recipientFullName: string;
  streetName: string;
  streetNumber: string;
  streetComplement: string;
  commune: string;
  region: string;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  shipDate: string;
  timeSlot: ITimeSlot;
};

type TBillingSummaryDeliveries = TBillingDeliveries;

type TOrderConfirmationDeliveries = Omit<TBillingDeliveries, 'homeDelivery'> & {
  homeDelivery?: THomeDeliveryOrderConfirmation[];
  pickUp?: TPickupDelivery[];
  intangible?: TIntangibleDeliveryWithoutEmail[];
  novios?: TNoviosDeliveryWithoutEmail[];
};

type TOrderConfirmationDelivery = NonNullable<
  TOrderConfirmationDeliveries[keyof TOrderConfirmationDeliveries]
>[number];

type THomeDeliveryOrderConfirmation = Omit<THomeDelivery, 'addressAlias'>;

type TPickupDelivery = {
  storeId?: string;
  shippingMethodType: string;
  shippingServiceName: string;
  pickupStoreName: string;
  pickupStoreStreetName: string;
  pickupStoreStreetNumber: string;
  pickupStoreCommune: string;
  proxyPickup: boolean;
  recipientFullName: string;
  deliveryDateString: string;
};

type TIntangibleDelivery = {
  shippingMethodType: string;
  customerEmail: string;
};

type TNoviosDelivery = {
  shippingMethodType: typeof SHIPPING_METHOD_TYPE.NOVIOS;
  customerEmail: string;
};

type TIntangibleDeliveryWithoutEmail = Omit<
  TIntangibleDelivery,
  'customerEmail'
>;
type TNoviosDeliveryWithoutEmail = Omit<TNoviosDelivery, 'customerEmail'>;

export interface Recefe {
  quantity?: number;
  deliveryPersonName?: string;
  addressName?: string;
  addressDescription?: string;
  deliveryDate?: string;
  addressLabelDetails?: string;
}

export interface PickUp {
  quantity?: number;
  deliveryPersonName?: string;
  addressName?: string;
  addressDescription?: string;
  deliveryDate?: string;
}

export interface Email {
  quantity?: number;
  email?: string;
}

export interface ISavedCard {
  paymentMethodId: string;
  paymentInstrumentId: string;
  cardType: string;
  numberLastDigits: string;
  isExpired: boolean;
  bankName: string;
  isCencosudCard: boolean;
  isCreditCard: boolean;
  selected: boolean;
  sortView: number;
}

export interface IDiscountCouponRequest {
  basketId: string;
  couponId: string;
}

export type TBillingProductItem = {
  arrivalDate?: string;
  arrivalDateEnd?: string;
  shipmentId?: string;
  gtm?: unknown;
  img?: string;
  imgAlt?: string;
  isIntangible?: boolean;
  isSampling?: boolean;
  isSecondHand?: boolean;
  options?: TBillingProductOption[];
  quantity?: number;
  specification?: string;
  sku?: string;
  title?: string;
  isPresale?: boolean;
  isColabPrice?: boolean;
  duration?: string;
};

type TBillingProductOption = {
  duration: string;
  quantity: number;
  serviceType: EServiceType;
  title: string;
  price?: number;
  priceFormat?: string;
};

type TBillingProductPrice = {
  discountPercentage?: string;
  id: string;
  isCencoPrice?: boolean;
  isColabPrice?: boolean;
  isCencoPayPrice?: boolean;
  isStrikedThrough?: boolean;
  price: number;
  priceFormat: string;
};

type TBillingSummaryProductPrice = TBillingProductPrice;

type TBillingOrderConfirmationPriceDetails = {
  price: string;
  isCencoPrice: boolean;
  isColabPrice: boolean;
};

type TBillingSummaryProductItem = TBillingProductItem & {
  prices?: TBillingSummaryProductPrice[];
  gtm?: TBillingSummaryProductGtm;
} & Item;

type TBillingOrderConfirmationProductItem = TBillingProductItem &
  Item & {
    price: TBillingOrderConfirmationPriceDetails;
    sellerId: string;
    gtm?: TBillingOrderConfirmationProductGtm;
    priceDetails?: TBillingOrderConfirmationPriceDetails;
    custom?: ICustomFields;
    services?: ProductServiceItemProps[];
    productKey?: string;
  };

export interface IItemGTM {
  brand: string;
  cencosudPrice: number; // dimension20
  claseId: string;
  claseName: string;
  departmentId: string;
  departmentName: string;
  hasWarranty: boolean;
  isCmedia: boolean;
  masterId: string;
  primaryCategoryId: string;
  rating: string;
  referencePrice: number;
  reviewsCount: number;
  shippingType: string;
  size: string;
  subDepartmentId: string;
  subDepartmentName: string;
}

type TBillingProductGtm = {
  brand: string;
  cencosudPrice: number;
  claseId: string;
  claseName: string;
  departmentId: string;
  departmentName: string;
  hasWarranty: boolean;
  isCmedia: boolean;
  isIntangible: boolean;
  masterId: string;
  price?: number;
  primaryCategoryId: string;
  referencePrice: number;
  reviewsCount: number;
  shippingType: string;
  size: string;
  subDepartmentName: string;
  subDepartmentId: string;
  rating?: string;
};

type TBillingSummaryProductGtm = TBillingProductGtm & {
  rating: string;
};

type TBillingOrderConfirmationProductGtm = TBillingProductGtm;

export type {
  TBillingDeliveries,
  TBillingOrderConfirmationPriceDetails,
  TBillingOrderConfirmationProductGtm,
  TBillingOrderConfirmationProductItem,
  TBillingProductOption,
  TBillingSummaryDeliveries,
  TBillingSummaryProductGtm,
  TBillingSummaryProductItem,
  TBillingSummaryProductPrice,
  TDeliveriesType,
  THomeDelivery,
  THomeDeliveryOrderConfirmation,
  TIntangibleDelivery,
  TIntangibleDeliveryWithoutEmail,
  TNoviosDelivery,
  TNoviosDeliveryWithoutEmail,
  TOrderConfirmationDeliveries,
  TOrderConfirmationDelivery,
  TPickupDelivery
};
